/**
 * The six areas of expertise. URLs stay under /specialisations for continuity;
 * the visible label everywhere is "Expertise".
 */
export type ExpertiseSlug =
  | "pricing-and-revenue-management"
  | "planning-budgeting-and-forecasting"
  | "roles-targets-and-incentives"
  | "project-and-commercial-control"
  | "procurement-and-supplier-costs"
  | "systems-selection-and-implementation";

export type ServiceSlug =
  | "operating-system"
  | "team-structure"
  | "strategy-execution"
  | "performance-discipline"
  | "project-leadership"
  | "systems-improvement";

export const expertiseAreas: {
  slug: ExpertiseSlug;
  name: string;
  to: string;
  summary: string;
}[] = [
  {
    slug: "pricing-and-revenue-management",
    name: "Pricing and revenue management",
    to: "/specialisations/pricing-and-revenue-management",
    summary:
      "See where margin is leaking, which discounts are doing the damage and what each price move is worth before you make it.",
  },
  {
    slug: "planning-budgeting-and-forecasting",
    name: "Planning, budgeting and forecasting",
    to: "/specialisations/planning-budgeting-and-forecasting",
    summary:
      "Know what cash looks like over the coming months, what is driving the budget and when the plan needs to change.",
  },
  {
    slug: "roles-targets-and-incentives",
    name: "Roles, targets and incentives",
    to: "/specialisations/roles-targets-and-incentives",
    summary:
      "Know who owns each result, how it is measured and whether a bonus or commission will pay for itself.",
  },
  {
    slug: "project-and-commercial-control",
    name: "Project and commercial control",
    to: "/specialisations/project-and-commercial-control",
    summary:
      "See what each job will cost to finish, which variations are unbilled and where margin is moving before completion.",
  },
  {
    slug: "procurement-and-supplier-costs",
    name: "Procurement and supplier costs",
    to: "/specialisations/procurement-and-supplier-costs",
    summary:
      "See where supplier and software spend has spread, what can be renegotiated and what the business should stop buying.",
  },
  {
    slug: "systems-selection-and-implementation",
    name: "Systems selection and implementation",
    to: "/specialisations/systems-selection-and-implementation",
    summary:
      "Choose a system against what the business needs, plan the change and make sure the team stops using the old workaround.",
  },
];

export const expertiseBySlug = (slug: ExpertiseSlug) =>
  expertiseAreas.find((a) => a.slug === slug)!;

/** Which service pages and priced packages deliver each area of expertise. */
export const deliveredThrough: Record<ExpertiseSlug, { label: string; to: string }[]> = {
  "pricing-and-revenue-management": [
    { label: "Performance & Commercial Discipline", to: "/services/performance-discipline" },
    { label: "Pricing and Margin Review", to: "/packages" },
  ],
  "planning-budgeting-and-forecasting": [
    { label: "Strategy to Execution", to: "/services/strategy-execution" },
    { label: "Performance & Commercial Discipline", to: "/services/performance-discipline" },
    { label: "Annual Operating Budget and Rolling Cash Forecast", to: "/packages" },
  ],
  "roles-targets-and-incentives": [
    { label: "People, Culture & Accountability", to: "/services/team-structure" },
    { label: "Org Design Sprint and Remuneration and Incentive Design", to: "/packages" },
  ],
  "project-and-commercial-control": [
    { label: "Project & Initiative Leadership", to: "/services/project-leadership" },
    { label: "Performance & Commercial Discipline", to: "/services/performance-discipline" },
    { label: "Leadership Scorecard Build", to: "/packages" },
  ],
  "procurement-and-supplier-costs": [
    { label: "Systems & Operational Improvement", to: "/services/systems-improvement" },
    { label: "Performance & Commercial Discipline", to: "/services/performance-discipline" },
    { label: "One process, one fix", to: "/packages" },
  ],
  "systems-selection-and-implementation": [
    { label: "Systems & Operational Improvement", to: "/services/systems-improvement" },
    { label: "Operating System & Governance", to: "/services/operating-system" },
    { label: "Service line install", to: "/packages" },
  ],
};

/** Which areas of expertise sit behind each service line. */
export const relatedExpertise: Record<ServiceSlug, ExpertiseSlug[]> = {
  "operating-system": ["planning-budgeting-and-forecasting", "systems-selection-and-implementation"],
  "team-structure": ["roles-targets-and-incentives"],
  "strategy-execution": ["planning-budgeting-and-forecasting", "pricing-and-revenue-management"],
  "performance-discipline": [
    "pricing-and-revenue-management",
    "project-and-commercial-control",
    "procurement-and-supplier-costs",
  ],
  "project-leadership": ["project-and-commercial-control", "systems-selection-and-implementation"],
  "systems-improvement": [
    "systems-selection-and-implementation",
    "procurement-and-supplier-costs",
  ],
};
